/**
 * Guide Detection
 *
 * Pure functions for detecting alignment and spacing guides.
 * No side effects, no DOM — just geometry.
 */

import type { Point, AlignmentGuide, SpacingGuide, NodeEdges } from './types';

// =============================================================================
// Node Edges
// =============================================================================

/** Compute the 6 edge/center coordinates from a node's position and size. */
export function getNodeEdges(node: { position: Point; size: { width: number; height: number } }): NodeEdges {
	const { x, y } = node.position;
	const { width, height } = node.size;
	return {
		top: y,
		bottom: y + height,
		left: x,
		right: x + width,
		centerX: x + width / 2,
		centerY: y + height / 2,
	};
}

// =============================================================================
// Alignment Guide Detection
// =============================================================================

/** Edge names for horizontal guides (y-axis alignment) */
const H_EDGES = ['top', 'bottom', 'centerY'] as const;

/** Edge names for vertical guides (x-axis alignment) */
const V_EDGES = ['left', 'right', 'centerX'] as const;

/**
 * Find alignment guides between dragged node bounds and candidate nodes.
 *
 * Checks all 6 dragged edges against all 6 candidate edges.
 * Returns guides where |delta| < threshold.
 */
export function findAlignmentGuides(
	draggedBounds: NodeEdges,
	candidates: Array<{ id: string; position: Point; size: { width: number; height: number } }>,
	threshold: number,
): AlignmentGuide[] {
	const guides: AlignmentGuide[] = [];

	for (const candidate of candidates) {
		const cb = getNodeEdges(candidate);

		// Horizontal guides (y-axis): dragged top/bottom/centerY vs candidate top/bottom/centerY
		for (const dEdge of H_EDGES) {
			for (const cEdge of H_EDGES) {
				const delta = Math.abs(draggedBounds[dEdge] - cb[cEdge]);
				if (delta < threshold) {
					const isCenter = dEdge === 'centerY' || cEdge === 'centerY';
					guides.push({
						axis: 'y',
						position: cb[cEdge],
						sourceEdge: dEdge,
						type: isCenter ? 'center' : 'edge',
						referenceNodeIds: [candidate.id],
					});
				}
			}
		}

		// Vertical guides (x-axis): dragged left/right/centerX vs candidate left/right/centerX
		for (const dEdge of V_EDGES) {
			for (const cEdge of V_EDGES) {
				const delta = Math.abs(draggedBounds[dEdge] - cb[cEdge]);
				if (delta < threshold) {
					const isCenter = dEdge === 'centerX' || cEdge === 'centerX';
					guides.push({
						axis: 'x',
						position: cb[cEdge],
						sourceEdge: dEdge,
						type: isCenter ? 'center' : 'edge',
						referenceNodeIds: [candidate.id],
					});
				}
			}
		}
	}

	return guides;
}

// =============================================================================
// Spacing Guide Detection
// =============================================================================

interface NodeInterval {
	id: string;
	start: number; // left or top
	end: number;   // right or bottom
	crossStart: number; // top or left (cross-axis)
	crossEnd: number;   // bottom or right (cross-axis)
}

/** Get the cross-axis overlap midpoint between two intervals. */
function crossOverlapCenter(a: NodeInterval, b: NodeInterval): number {
	const overlapStart = Math.max(a.crossStart, b.crossStart);
	const overlapEnd = Math.min(a.crossEnd, b.crossEnd);
	return (overlapStart + overlapEnd) / 2;
}

/**
 * Find equal-spacing guides.
 *
 * For each axis, finds pairs of adjacent candidate nodes with gap G,
 * then checks if placing the dragged node creates gap G to its neighbors.
 */
export function findSpacingGuides(
	draggedBounds: NodeEdges,
	candidates: Array<{ id: string; position: Point; size: { width: number; height: number } }>,
	threshold: number,
): SpacingGuide[] {
	const guides: SpacingGuide[] = [];

	// Check horizontal spacing (x-axis: left→right ordering)
	const hGuides = findSpacingOnAxis(
		draggedBounds,
		candidates,
		threshold,
		'x',
	);
	guides.push(...hGuides);

	// Check vertical spacing (y-axis: top→bottom ordering)
	const vGuides = findSpacingOnAxis(
		draggedBounds,
		candidates,
		threshold,
		'y',
	);
	guides.push(...vGuides);

	return guides;
}

function findSpacingOnAxis(
	draggedBounds: NodeEdges,
	candidates: Array<{ id: string; position: Point; size: { width: number; height: number } }>,
	threshold: number,
	axis: 'x' | 'y',
): SpacingGuide[] {
	const results: SpacingGuide[] = [];

	// Build intervals for candidates on this axis
	const intervals: NodeInterval[] = candidates.map((c) => {
		const edges = getNodeEdges(c);
		if (axis === 'x') {
			return { id: c.id, start: edges.left, end: edges.right, crossStart: edges.top, crossEnd: edges.bottom };
		} else {
			return { id: c.id, start: edges.top, end: edges.bottom, crossStart: edges.left, crossEnd: edges.right };
		}
	});

	// Dragged node interval
	const dragInterval: NodeInterval = axis === 'x'
		? { id: '__dragged__', start: draggedBounds.left, end: draggedBounds.right, crossStart: draggedBounds.top, crossEnd: draggedBounds.bottom }
		: { id: '__dragged__', start: draggedBounds.top, end: draggedBounds.bottom, crossStart: draggedBounds.left, crossEnd: draggedBounds.right };

	// Filter candidates that overlap the dragged node's cross-axis range (with some margin)
	const crossOverlapping = intervals.filter((iv) =>
		iv.crossEnd > dragInterval.crossStart - threshold &&
		iv.crossStart < dragInterval.crossEnd + threshold
	);

	if (crossOverlapping.length < 1) return results;

	// Sort by start position
	crossOverlapping.sort((a, b) => a.start - b.start);

	// Find gaps between adjacent candidate pairs
	const gaps: Array<{ from: NodeInterval; to: NodeInterval; gap: number }> = [];
	for (let i = 0; i < crossOverlapping.length - 1; i++) {
		const a = crossOverlapping[i];
		const b = crossOverlapping[i + 1];
		const gap = b.start - a.end;
		if (gap > 0) {
			gaps.push({ from: a, to: b, gap });
		}
	}

	if (gaps.length === 0) return results;

	// For each gap, check if the dragged node can be placed to create the same gap
	// Case 1: dragged is to the LEFT of a candidate — gap from dragged.end to candidate.start
	// Case 2: dragged is to the RIGHT of a candidate — gap from candidate.end to dragged.start
	for (const { from, to, gap } of gaps) {
		// Check: dragged is to the left of 'from' with matching gap
		const gapLeftOfFrom = from.start - dragInterval.end;
		if (Math.abs(gapLeftOfFrom - gap) < threshold) {
			results.push({
				axis,
				gap,
				segments: [
					{ fromEdge: dragInterval.end, toEdge: from.start, center: crossOverlapCenter(dragInterval, from) },
					{ fromEdge: from.end, toEdge: to.start, center: crossOverlapCenter(from, to) },
				],
			});
		}

		// Check: dragged is to the right of 'to' with matching gap
		const gapRightOfTo = dragInterval.start - to.end;
		if (Math.abs(gapRightOfTo - gap) < threshold) {
			results.push({
				axis,
				gap,
				segments: [
					{ fromEdge: from.end, toEdge: to.start, center: crossOverlapCenter(from, to) },
					{ fromEdge: to.end, toEdge: dragInterval.start, center: crossOverlapCenter(to, dragInterval) },
				],
			});
		}

		// Check: dragged is between 'from' and 'to'
		// Gap from.end→dragged.start and dragged.end→to.start should both equal the same value
		const gapBefore = dragInterval.start - from.end;
		const gapAfter = to.start - dragInterval.end;
		if (gapBefore > 0 && gapAfter > 0 && Math.abs(gapBefore - gapAfter) < threshold) {
			// Check if this gap is close to any existing pair gap
			const avgGap = (gapBefore + gapAfter) / 2;
			if (Math.abs(avgGap - gap) < threshold) {
				results.push({
					axis,
					gap,
					segments: [
						{ fromEdge: from.end, toEdge: dragInterval.start, center: crossOverlapCenter(from, dragInterval) },
						{ fromEdge: dragInterval.end, toEdge: to.start, center: crossOverlapCenter(dragInterval, to) },
					],
				});
			}
		}
	}

	return results;
}
